import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  User,
  Mail,
  Phone,
  MapPin,
  Lock,
  Bell,
  Save,
  AlertCircle,
  Check
} from 'lucide-react'
import api from '../config/api'
import { useAuth } from '../hooks/useAuth'
import '../styles/Profile.css'

function Profile() {
  const { client } = useAuth()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [activeTab, setActiveTab] = useState('profile')

  // Form states
  const [formData, setFormData] = useState({
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: ''
  })

  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    smsNotifications: false,
    portalNotifications: true,
    documentUpdates: true,
    messageNotifications: true,
    paymentReminders: true,
    appointmentReminders: true
  })

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      setLoading(true)
      const response = await api.get('/profile')
      const data = response.data.data
      setProfile(data)
      setFormData({
        phone: data.phone || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        zipCode: data.zipCode || ''
      })
      setPreferences({
        emailNotifications: true,
        smsNotifications: false,
        portalNotifications: true,
        documentUpdates: true,
        messageNotifications: true,
        paymentReminders: true,
        appointmentReminders: true,
        ...(data.communicationPreferences || {})
      })
    } catch (err) {
      setError('Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const handleProfileUpdate = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setSaving(true)

    try {
      await api.put('/profile', {
        ...formData,
        communicationPreferences: preferences
      })
      setSuccess('Profile updated successfully')
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handlePreferencesUpdate = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setSaving(true)

    try {
      await api.put('/profile', {
        communicationPreferences: preferences
      })
      setSuccess('Notification preferences saved')
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save preferences')
    } finally {
      setSaving(false)
    }
  }

  const handlePasswordChange = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match')
      return
    }

    if (passwordData.newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setSaving(true)

    try {
      await api.post('/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      })
      setSuccess('Password changed successfully')
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password')
    } finally {
      setSaving(false)
    }
  }

  const togglePreference = (key) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading profile...</p>
      </div>
    )
  }

  return (
    <div className="profile-page">
      <header className="page-header">
        <div className="header-content">
          <User size={28} />
          <div>
            <h1>My Profile</h1>
            <p>Manage your account settings</p>
          </div>
        </div>
      </header>

      {/* Profile Card */}
      <div className="profile-card">
        <div className="profile-avatar">
          {client?.firstName?.[0]}{client?.lastName?.[0]}
        </div>
        <div className="profile-info">
          <h2>{profile?.firstName} {profile?.lastName}</h2>
          <p className="profile-email">
            <Mail size={16} />
            {profile?.email}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="profile-tabs">
        <button
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <User size={18} />
          Profile
        </button>
        <button
          className={`tab ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          <Bell size={18} />
          Notifications
        </button>
        <button
          className={`tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          <Lock size={18} />
          Security
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <motion.div
          className="alert alert-error"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <AlertCircle size={18} />
          <span>{error}</span>
        </motion.div>
      )}
      {success && (
        <motion.div
          className="alert alert-success"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Check size={18} />
          <span>{success}</span>
        </motion.div>
      )}

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'profile' && (
          <motion.form
            className="settings-form"
            onSubmit={handleProfileUpdate}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <h3>Contact Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Phone Number</label>
                <div className="input-wrapper">
                  <Phone size={18} className="input-icon" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="0917 123 4567"
                  />
                </div>
              </div>
              <div className="form-group full-width">
                <label>Street Address</label>
                <div className="input-wrapper">
                  <MapPin size={18} className="input-icon" />
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Katipunan Ave."
                  />
                </div>
              </div>
              <div className="form-group">
                <label>City / Municipality</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Quezon City"
                />
              </div>
              <div className="form-group">
                <label>Province / Region</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="Metro Manila"
                />
              </div>
              <div className="form-group">
                <label>Postal Code</label>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  placeholder="1101"
                />
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </motion.form>
        )}

        {activeTab === 'notifications' && (
          <motion.form
            className="settings-form"
            onSubmit={handlePreferencesUpdate}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <h3>Notification Channels</h3>
            <p className="section-desc">Choose how you want to receive notifications</p>
            <div className="preferences-list">
              <label className="preference-item">
                <div className="preference-info">
                  <span className="preference-label">Email Notifications</span>
                  <span className="preference-desc">
                    Receive updates about your matters via email
                  </span>
                </div>
                <div
                  className={`toggle ${preferences.emailNotifications ? 'toggle-on' : ''}`}
                  onClick={() => togglePreference('emailNotifications')}
                  role="switch"
                  aria-checked={preferences.emailNotifications}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && togglePreference('emailNotifications')}
                >
                  <div className="toggle-thumb" />
                </div>
              </label>
              <label className="preference-item">
                <div className="preference-info">
                  <span className="preference-label">Portal Notifications</span>
                  <span className="preference-desc">
                    Show notifications within the client portal
                  </span>
                </div>
                <div
                  className={`toggle ${preferences.portalNotifications ? 'toggle-on' : ''}`}
                  onClick={() => togglePreference('portalNotifications')}
                  role="switch"
                  aria-checked={preferences.portalNotifications}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && togglePreference('portalNotifications')}
                >
                  <div className="toggle-thumb" />
                </div>
              </label>
            </div>

            <h3 className="section-heading">Notification Types</h3>
            <p className="section-desc">Select which notifications you want to receive</p>
            <div className="preferences-list">
              <label className="preference-item">
                <div className="preference-info">
                  <span className="preference-label">Document Updates</span>
                  <span className="preference-desc">
                    Notifications when documents are shared, signed, or updated
                  </span>
                </div>
                <div
                  className={`toggle ${preferences.documentUpdates ? 'toggle-on' : ''}`}
                  onClick={() => togglePreference('documentUpdates')}
                  role="switch"
                  aria-checked={preferences.documentUpdates}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && togglePreference('documentUpdates')}
                >
                  <div className="toggle-thumb" />
                </div>
              </label>
              <label className="preference-item">
                <div className="preference-info">
                  <span className="preference-label">Message Notifications</span>
                  <span className="preference-desc">
                    Alerts when you receive new messages from your legal team
                  </span>
                </div>
                <div
                  className={`toggle ${preferences.messageNotifications ? 'toggle-on' : ''}`}
                  onClick={() => togglePreference('messageNotifications')}
                  role="switch"
                  aria-checked={preferences.messageNotifications}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && togglePreference('messageNotifications')}
                >
                  <div className="toggle-thumb" />
                </div>
              </label>
              <label className="preference-item">
                <div className="preference-info">
                  <span className="preference-label">Payment Reminders</span>
                  <span className="preference-desc">
                    Reminders about upcoming or overdue invoice payments
                  </span>
                </div>
                <div
                  className={`toggle ${preferences.paymentReminders ? 'toggle-on' : ''}`}
                  onClick={() => togglePreference('paymentReminders')}
                  role="switch"
                  aria-checked={preferences.paymentReminders}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && togglePreference('paymentReminders')}
                >
                  <div className="toggle-thumb" />
                </div>
              </label>
              <label className="preference-item">
                <div className="preference-info">
                  <span className="preference-label">Appointment Reminders</span>
                  <span className="preference-desc">
                    Reminders about upcoming appointments and consultations
                  </span>
                </div>
                <div
                  className={`toggle ${preferences.appointmentReminders ? 'toggle-on' : ''}`}
                  onClick={() => togglePreference('appointmentReminders')}
                  role="switch"
                  aria-checked={preferences.appointmentReminders}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && togglePreference('appointmentReminders')}
                >
                  <div className="toggle-thumb" />
                </div>
              </label>
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Preferences'}
            </button>
          </motion.form>
        )}

        {activeTab === 'security' && (
          <motion.form
            className="settings-form"
            onSubmit={handlePasswordChange}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <h3>Change Password</h3>
            <div className="form-grid single-column">
              <div className="form-group">
                <label>Current Password</label>
                <div className="input-wrapper">
                  <Lock size={18} className="input-icon" />
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({
                      ...passwordData,
                      currentPassword: e.target.value
                    })}
                    placeholder="Enter current password"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>New Password</label>
                <div className="input-wrapper">
                  <Lock size={18} className="input-icon" />
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({
                      ...passwordData,
                      newPassword: e.target.value
                    })}
                    placeholder="Enter new password"
                    required
                    minLength={8}
                  />
                </div>
                <span className="form-hint">Minimum 8 characters</span>
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <div className="input-wrapper">
                  <Lock size={18} className="input-icon" />
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({
                      ...passwordData,
                      confirmPassword: e.target.value
                    })}
                    placeholder="Confirm new password"
                    required
                  />
                </div>
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <Lock size={18} />
              {saving ? 'Updating...' : 'Update Password'}
            </button>
          </motion.form>
        )}
      </div>
    </div>
  )
}

export default Profile
